import {HubCollectionSummary} from '@/components/admin/HubCollectionSummary';
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useGetAdminStats,
  getGetAdminStatsQueryKey,
  useGetAdminActivityHistory,
  useGetAdminGrowthHistory,
  getGetAdminActivityHistoryQueryKey,
  getGetAdminGrowthHistoryQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import type { DateRange } from "react-day-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Link, Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Monitor, Smartphone } from "lucide-react";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

type StoreOrderStat = { storeId: number; storeName: string; today: number; week: number; allTime: number };

function formatHour(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDay(date: string) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

const STAT_CARDS = [
  { key: "totalUsers",        label: "Total Users",       highlight: false, ydayKey: "totalUsersYesterday",          l7Key: "totalUsersLast7" },
  { key: "pendingApprovals",  label: "Pending Approvals", highlight: true,  ydayKey: null,                           l7Key: null },
  { key: "totalBusinesses",   label: "Businesses",        highlight: false, ydayKey: "totalBusinessesYesterday",     l7Key: "totalBusinessesLast7" },
  { key: "totalJobs",         label: "Jobs",              highlight: false, ydayKey: "totalJobsYesterday",           l7Key: "totalJobsLast7" },
  { key: "totalEvents",       label: "Events",            highlight: false, ydayKey: "totalEventsYesterday",         l7Key: "totalEventsLast7" },
  { key: "totalDeals",        label: "Deals",             highlight: false, ydayKey: "totalDealsYesterday",          l7Key: "totalDealsLast7" },
  { key: "totalNews",         label: "News Articles",     highlight: false, ydayKey: "totalNewsYesterday",           l7Key: "totalNewsLast7" },
  { key: "totalTradespeople", label: "Trade Profiles",    highlight: false, ydayKey: "totalTradespeopleYesterday",   l7Key: "totalTradespeopleL7" },
  { key: "totalLeadsUnlocked", label: "Leads Unlocked",  highlight: false, ydayKey: "totalLeadsUnlockedYesterday",  l7Key: "totalLeadsUnlockedLast7" },
] as const;

/** Local calendar date. toISOString() rolls back a day through BST before 01:00. */
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const todayISO = () => toISODate(new Date());

export default function AdminDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const [growthDays, setGrowthDays] = useState<7 | 14 | 30 | 90>(30);
  const { data: stats, isLoading } = useGetAdminStats({ query: { queryKey: getGetAdminStatsQueryKey(), enabled: user?.role === "admin" } });
  const { data: activityHistory } = useGetAdminActivityHistory({
    query: { queryKey: getGetAdminActivityHistoryQueryKey(), enabled: user?.role === "admin", refetchInterval: 5 * 60 * 1000, staleTime: 4 * 60 * 1000 },
  });
  const { data: growthHistory, isLoading: growthLoading } = useGetAdminGrowthHistory(
    { days: growthDays },
    { query: { queryKey: getGetAdminGrowthHistoryQueryKey({ days: growthDays }), enabled: user?.role === "admin", staleTime: 5 * 60 * 1000 } },
  );
  const token = localStorage.getItem("ayrshire_token");

  type TodayVsLwHour = { hour: string; todayUsers: number; todayOrders: number; lastWeekUsers: number; lastWeekOrders: number; isFuture: boolean };
  const { data: todayVsLw } = useQuery<{ hours: TodayVsLwHour[] }>({
    queryKey: ["admin-today-vs-last-week"],
    enabled: user?.role === "admin",
    staleTime: 3 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: () => fetch("/api/admin/today-vs-last-week", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()) as Promise<{ hours: TodayVsLwHour[] }>,
  });

  /* Day-by-day money, so the dashboard can answer "how did last Tuesday go"
     without opening the Hub Revenue page. Both read getDailyHubSummary /
     getHubReconciliation, which share their sources, so the two screens cannot
     disagree about the same day. */
  type HubRevenueBreakdown = import("@workspace/api-client-react").HubRevenueBreakdown;
  type HubDay = { date: string; orders: number; transactedPence: number; retainedPence: number; breakdown: HubRevenueBreakdown };
  const [rangeOpen, setRangeOpen] = useState(false);
  /* The picker's in-progress selection, kept separate from the applied range so
     a half-finished pick never drives the figures below. */
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const [pickedStart, setPickedStart] = useState(false);
  // Two months side by side overflows a phone; show one there.
  const twoMonths = typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches;
  /* Opens on today. The dashboard's job is "how are we doing right now"; a
     two-week window as the default answered a question nobody had asked yet and
     buried today's figures in an average. The range picker is there for when a
     longer view is actually wanted. */
  const [revFrom, setRevFrom] = useState(() => todayISO());
  const [revTo, setRevTo] = useState(() => todayISO());
  const { data: hubDays, isLoading: hubDaysLoading } = useQuery<{ days: HubDay[] }>({
    queryKey: ["admin-daily-summary", revFrom, revTo],
    enabled: user?.role === "admin",
    staleTime: 60 * 1000,
    queryFn: () => {
      const qs = new URLSearchParams({
        from: new Date(`${revFrom}T00:00:00`).toISOString(),
        to: new Date(`${revTo}T23:59:59`).toISOString(),
      });
      return fetch(`/api/admin/daily-summary?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()) as Promise<{ days: HubDay[] }>;
    },
  });

  const rangeLabel = (() => {
    const f = new Date(`${revFrom}T12:00:00`), t = new Date(`${revTo}T12:00:00`);
    const d = (x: Date, withYear = false) =>
      x.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
    if (revFrom === revTo) return d(f, true);
    return `${d(f)} – ${d(t, true)}`;
  })();

  const { data: storeStats } = useQuery<StoreOrderStat[]>({
    queryKey: ["admin-store-order-stats"],
    enabled: user?.role === "admin",
    staleTime: 60 * 1000,
    queryFn: () => fetch("/api/admin/store-order-stats", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()) as Promise<StoreOrderStat[]>,
  });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const web1h     = stats?.activeWeb1h     ?? 0;
  const mobile1h  = stats?.activeMobile1h  ?? 0;
  const web24h    = stats?.activeWeb24h    ?? 0;
  const mobile24h = stats?.activeMobile24h ?? 0;

  return (
    <AdminLayout current="/admin">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
          {(stats?.pendingApprovals ?? 0) > 0 && (
            <Link href="/admin/pending">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer">
                {stats?.pendingApprovals} items need approval
              </span>
            </Link>
          )}
        </div>

        {/* Orders & New Users quick-stat strip */}
        {!isLoading && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Orders today",     value: stats.ordersToday,     vs: stats.ordersSameDayLastWeek,    accent: "text-orange-600" },
              { label: "Orders yesterday", value: stats.ordersYesterday, vs: null,                           accent: "text-orange-400" },
              { label: "Orders this week", value: stats.ordersThisWeek,  vs: stats.ordersSameWeekLastWeek,   accent: "text-orange-600" },
              { label: "New users today",  value: stats.newUsersToday,   vs: stats.newUsersSameDayLastWeek,  accent: "text-blue-600" },
              { label: "New users week",   value: stats.newUsersThisWeek, vs: stats.newUsersSameWeekLastWeek, accent: "text-blue-600" },
            ].map(({ label, value, vs, accent }) => {
              // Counters are optional in the schema; a missing one is no trend
              // rather than NaN.
              const diff = vs != null && value != null ? value - vs : null;
              return (
                <div key={label} className="rounded-xl border bg-card px-4 py-3 flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">{label}</span>
                  <span className={`text-2xl font-extrabold ${accent}`}>{value}</span>
                  {vs != null && (
                    <span className={`text-xs font-semibold ${diff! > 0 ? "text-green-600" : diff! < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                      {diff! > 0 ? "↑" : diff! < 0 ? "↓" : "="} {vs} {label.toLowerCase().includes("week") ? "same point last week" : "last week"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* New vs returning — the health signal a raw order count hides.
            "Returning" means they had ordered BEFORE this week, matched on user
            id or phone so anonymous checkout does not reset a regular to new. */}
        {!isLoading && stats && (stats.ordersFromNewCustomers ?? 0) + (stats.ordersFromReturningCustomers ?? 0) > 0 && (() => {
          const nw = stats.ordersFromNewCustomers ?? 0;
          const rt = stats.ordersFromReturningCustomers ?? 0;
          const tot = nw + rt;
          const pct = Math.round((rt / tot) * 100);
          return (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Customers this week</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-end gap-6">
                  <div>
                    <div className="text-3xl font-extrabold text-emerald-600">{pct}%</div>
                    <div className="text-xs text-muted-foreground">of orders from returning customers</div>
                  </div>
                  <div className="flex gap-5 text-sm">
                    <div><span className="font-bold text-emerald-600">{rt}</span> <span className="text-muted-foreground">returning</span></div>
                    <div><span className="font-bold text-sky-600">{nw}</span> <span className="text-muted-foreground">new</span></div>
                    <div><span className="font-bold">{tot}</span> <span className="text-muted-foreground">total</span></div>
                  </div>
                </div>
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" title={`${rt} returning / ${nw} new`}>
                  <div className="bg-emerald-500" style={{ width: `${pct}%` }} />
                  <div className="bg-sky-400" style={{ width: `${100 - pct}%` }} />
                </div>
              </CardContent>
            </Card>
          );
        })()}


        {/* Revenue by day — a compact read of /admin/hub-revenue, with a date
            range so previous days can be checked without leaving the dashboard. */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
              <CardTitle className="text-base">Revenue by day</CardTitle>
              <div className="flex items-center gap-3 text-sm">
                {/* One calendar, drag out a range — pick the start, pick the end,
                    the way a holiday booking works. Two separate date inputs made
                    you set each end independently and never showed the span. */}
                <Popover
                  open={rangeOpen}
                  onOpenChange={(o) => {
                    setRangeOpen(o);
                    if (o) {
                      setDraft({ from: new Date(`${revFrom}T12:00:00`), to: new Date(`${revTo}T12:00:00`) });
                      setPickedStart(false);
                    } else if (draft?.from) {
                      // Dismissed mid-pick — keep what was chosen rather than
                      // discarding it and leaving the button showing something else.
                      setRevFrom(toISODate(draft.from));
                      setRevTo(toISODate(draft.to ?? draft.from));
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      className="inline-flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
                      aria-label="Choose a date range"
                    >
                      <CalendarIcon size={14} className="text-slate-400" />
                      <span className="font-medium text-slate-700 whitespace-nowrap">{rangeLabel}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto max-w-[calc(100vw-1.5rem)] p-0" align="end">
                    <Calendar
                      mode="range"
                      defaultMonth={new Date(`${revFrom}T12:00:00`)}
                      selected={draft}
                      // Future days hold no data and picking one only produces an
                      // empty table, so they are not selectable.
                      disabled={{ after: new Date() }}
                      numberOfMonths={twoMonths ? 2 : 1}
                      onSelect={(r) => {
                        setDraft(r);
                        /* Count the clicks rather than reading the range. On the
                           FIRST click react-day-picker returns { from: d, to: d }
                           — a one-day range — so testing `from && to` was true
                           immediately and the calendar shut before the second
                           date could be picked. */
                        if (!pickedStart) { setPickedStart(true); return; }
                        if (r?.from) {
                          setRevFrom(toISODate(r.from));
                          setRevTo(toISODate(r.to ?? r.from));
                          setPickedStart(false);
                          setRangeOpen(false);
                        }
                      }}
                    />
                    <div className="flex flex-wrap gap-1.5 border-t border-slate-100 p-2">
                      {[{ l: "Today", d: 0 }, { l: "7 days", d: 6 }, { l: "14 days", d: 13 }, { l: "30 days", d: 29 }].map(({ l, d }) => (
                        <button
                          key={l}
                          onClick={() => {
                            const f = new Date(Date.now() - d * 86400_000), t = new Date();
                            setRevFrom(toISODate(f)); setRevTo(toISODate(t));
                            setDraft({ from: f, to: t });
                            setPickedStart(false); setRangeOpen(false);
                          }}
                          className="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-200 hover:bg-slate-50"
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Link href="/admin/hub-revenue">
                  <span className="text-primary font-semibold hover:underline cursor-pointer whitespace-nowrap">Full report</span>
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {hubDaysLoading ? (
              <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
            ) : !hubDays?.days?.length ? (
              <p className="text-sm text-slate-400 py-6 text-center">No orders in this period.</p>
            ) : (() => {
              const days = hubDays.days;
              const totOrders = days.reduce((s, d) => s + d.orders, 0);
              const totTrans  = days.reduce((s, d) => s + d.transactedPence, 0);
              const totKept   = days.reduce((s, d) => s + d.retainedPence, 0);
              // Period totals for each source of "Recorded revenue". The components sum to totKept.
              const bd = days.reduce((a, d) => {
                const b = d.breakdown ?? {} as HubRevenueBreakdown;
                a.serviceFeePence      += b.serviceFeePence ?? 0;
                a.upliftPence          += b.upliftPence ?? 0;
                a.hubDeliveryFoodPence += b.hubDeliveryFoodPence ?? 0;
                a.tipsPence            += b.tipsPence ?? 0;
                a.hubDashPence         += b.hubDashPence ?? 0;
                a.externalDriverPence  += b.externalDriverPence ?? 0;
                a.cancellationPence    += b.cancellationPence ?? 0;
                a.weeklyShopFeesPence  += b.weeklyShopFeesPence ?? 0;
                a.adjustmentsPence     += b.adjustmentsPence ?? 0;
                return a;
              }, { serviceFeePence: 0, upliftPence: 0, hubDeliveryFoodPence: 0, tipsPence: 0, hubDashPence: 0, externalDriverPence: 0, cancellationPence: 0, weeklyShopFeesPence: 0, adjustmentsPence: 0 });
              const money = (p: number) => `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const keptLines: { label: string; pence: number }[] = [
                { label: "Service fee",            pence: bd.serviceFeePence },
                { label: "Menu-price uplift",      pence: bd.upliftPence },
                { label: "Internal driver request", pence: bd.hubDeliveryFoodPence },
                { label: "Tips (hub share)",       pence: bd.tipsPence },
                { label: "Hub Dash",               pence: bd.hubDashPence },
                { label: "External driver requests", pence: bd.externalDriverPence },
                { label: "Cancellation fees",      pence: bd.cancellationPence },
                { label: "Weekly shop fees",        pence: bd.weeklyShopFeesPence },
                { label: "Adjustments",            pence: bd.adjustmentsPence },
              ];
              const dayLabel = (iso: string) => {
                const d = new Date(`${iso}T12:00:00`);
                return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
              };
              return (
                <>
                  <HubCollectionSummary from={revFrom} to={revTo} recordedRevenuePence={totKept}/>
                  {/* Stacks on a phone — three money figures side by side at
                      390px truncates every one of them. */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mb-4">
                    <div className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Orders</div>
                      <div className="text-xl font-bold text-slate-800 mt-0.5">{totOrders}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Transacted</div>
                      <div className="text-xl font-bold text-slate-800 mt-0.5">{money(totTrans)}</div>
                      <div className="text-[11px] text-slate-400">all stores combined</div>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Recorded revenue</div>
                      <div className="text-xl font-bold text-emerald-800 mt-0.5">{money(totKept)}</div>
                      <div className="text-[11px] text-emerald-700/70">
                        Before costs; includes unpaid charges
                      </div>
                    </div>
                  </div>
                  {/* What we kept, broken down by source. The lines sum to the
                      "Recorded revenue" figure above; zero lines are hidden to cut noise. */}
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 mb-4">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">Recorded revenue — breakdown</div>
                    <div className="divide-y divide-emerald-100/70">
                      {keptLines.filter(l => l.pence !== 0).map(l => (
                        <div key={l.label} className="flex items-center justify-between py-1.5">
                          <span className="text-[13px] text-slate-600">{l.label}</span>
                          <span className={`text-[13px] font-medium tabular-nums whitespace-nowrap ${l.pence < 0 ? "text-rose-600" : "text-slate-800"}`}>{money(l.pence)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 mt-0.5">
                        <span className="text-[13px] font-bold text-emerald-800">Total recorded revenue</span>
                        <span className="text-[15px] font-bold text-emerald-800 tabular-nums whitespace-nowrap">{money(totKept)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                          <th className="text-left font-semibold py-2">Day</th>
                          <th className="text-right font-semibold py-2 w-14">Orders</th>
                          <th className="text-right font-semibold py-2">Transacted</th>
                          <th className="text-right font-semibold py-2">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {days.map(d => (
                          <tr key={d.date} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                            <td className="py-2 text-slate-700 whitespace-nowrap">{dayLabel(d.date)}</td>
                            <td className="py-2 text-right text-slate-500 tabular-nums">{d.orders}</td>
                            <td className="py-2 text-right text-slate-800 tabular-nums whitespace-nowrap">{money(d.transactedPence)}</td>
                            <td className="py-2 text-right font-semibold text-emerald-700 tabular-nums whitespace-nowrap">{money(d.retainedPence)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Per-store order counts */}
        {storeStats && storeStats.length > 0 && (
          <div className="rounded-xl border bg-card overflow-hidden">
            <div className="px-5 py-3 border-b bg-muted/30 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Orders by Store</span>
              <span className="text-xs text-muted-foreground">today · this week · all time</span>
            </div>
            <div className="divide-y">
              {storeStats.map((s) => (
                <div key={s.storeId} className="flex items-center gap-3 px-5 py-2.5">
                  <span className="flex-1 text-sm font-medium text-foreground truncate">{s.storeName}</span>
                  <span className={`text-sm font-bold w-8 text-right ${s.today > 0 ? "text-orange-600" : "text-muted-foreground"}`}>{s.today}</span>
                  <span className="text-xs text-muted-foreground w-16 text-right">{s.week} wk</span>
                  <span className="text-xs text-muted-foreground w-16 text-right">{s.allTime} total</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Users Panel */}
        {!isLoading && (
          <div className="rounded-xl border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              <h2 className="text-sm font-semibold text-foreground">Active Users</h2>
              <span className="text-xs text-muted-foreground">(heartbeat every 3 min)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Past 1 hour */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Past 1 hour</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Monitor className="h-4 w-4" />
                    Web
                  </div>
                  <span className="text-2xl font-bold text-foreground">{web1h}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Smartphone className="h-4 w-4" />
                    Mobile
                  </div>
                  <span className="text-2xl font-bold text-foreground">{mobile1h}</span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-medium">Total</span>
                  <span className="text-xl font-bold text-primary">{web1h + mobile1h}</span>
                </div>
              </div>

              {/* Past 24 hours */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Past 24 hours</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Monitor className="h-4 w-4" />
                    Web
                  </div>
                  <span className="text-2xl font-bold text-foreground">{web24h}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Smartphone className="h-4 w-4" />
                    Mobile
                  </div>
                  <span className="text-2xl font-bold text-foreground">{mobile24h}</span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-medium">Total</span>
                  <span className="text-xl font-bold text-primary">{web24h + mobile24h}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity over time graph */}
        {activityHistory && activityHistory.length > 0 && (
          <div className="rounded-xl border bg-card p-5 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Active Users — Last 24 Hours</h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={activityHistory.map(p => ({ ...p, hour: formatHour(p.hour) }))} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="webGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="mobileGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="web" name="Web" stroke="#3b82f6" fill="url(#webGrad)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="mobile" name="Mobile" stroke="#22c55e" fill="url(#mobileGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="rounded-xl border bg-card p-5 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Platform Growth</h2>
              <p className="text-xs text-muted-foreground mt-1">Daily registrations and non-cancelled orders</p>
            </div>
            <div className="flex rounded-lg border bg-muted/30 p-1">
              {([7, 14, 30, 90] as const).map(days => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setGrowthDays(days)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${growthDays === days ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          {growthLoading ? (
            <div className="h-52 flex items-center justify-center text-sm text-muted-foreground">Loading growth data...</div>
          ) : !growthHistory?.daily.length || growthHistory.daily.every(point => point.newUsers === 0 && point.orderCount === 0) ? (
            <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">No growth activity in this period.</div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New users per day</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={growthHistory.daily.map(point => ({ ...point, label: formatDay(point.date) }))} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="userGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="newUsers" name="New users" stroke="#2563eb" fill="url(#userGrowthGrad)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Orders per day</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={growthHistory.daily.map(point => ({ ...point, label: formatDay(point.date) }))} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="orderGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#d97706" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="orderCount" name="Orders" stroke="#d97706" fill="url(#orderGrowthGrad)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Today vs Same Day Last Week */}
          {todayVsLw && (
            <div className="border-t pt-5 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Today vs same day last week — by hour</h3>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">New users</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={todayVsLw.hours.filter(h => !h.isFuture || h.lastWeekUsers > 0)}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="todayUsers" name="Today" stroke="#2563eb" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="lastWeekUsers" name="Last week" stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Orders</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={todayVsLw.hours.filter(h => !h.isFuture || h.lastWeekOrders > 0)}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="todayOrders" name="Today" stroke="#d97706" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="lastWeekOrders" name="Last week" stroke="#fcd34d" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading stats...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAT_CARDS.map(({ key, label, highlight, ydayKey, l7Key }) => {
              const val = stats?.[key] ?? 0;
              const yday = ydayKey ? (stats?.[ydayKey as keyof typeof stats] as number ?? 0) : null;
              const l7   = l7Key   ? (stats?.[l7Key   as keyof typeof stats] as number ?? 0) : null;
              const delta = (n: number | null) => {
                if (n === null) return null;
                if (n > 0)  return <span className="text-green-600">↑{n}</span>;
                if (n === 0) return <span className="text-muted-foreground">–</span>;
                return <span className="text-red-500">↓{Math.abs(n)}</span>;
              };
              return (
              <Card key={key} className={highlight && val > 0 ? "border-amber-300" : ""}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${highlight && val > 0 ? "text-amber-600" : "text-foreground"}`}>
                    {val}
                  </div>
                  {(yday !== null || l7 !== null) && (
                    <div className="mt-2 flex flex-col gap-0.5">
                      {yday !== null && (
                        <div className="flex items-center gap-1 text-xs font-medium">
                          {delta(yday)}
                          <span className="text-muted-foreground">yesterday</span>
                        </div>
                      )}
                      {l7 !== null && (
                        <div className="flex items-center gap-1 text-xs font-medium">
                          {delta(l7)}
                          <span className="text-muted-foreground">last 7 days</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
